// ldos Contacts — the city-center CRM utility (work order S11, host side).
//
// This service backs the sdk.contacts namespace. Every read goes through the
// SECURITY DEFINER resolver ldos_resolve_contact_access(app_slug), which is
// the ONLY path that honours per-app visibility (ldos_contact_app_links):
// a pastoral contact granted to prayer-life must never surface in a business
// pipeline. Writes stamp field provenance so a later inbound sync cannot
// clobber a value a human just fixed (S11f failure mode 2).
//
// Phone identity uses libphonenumber-js/min — the `min` metadata build
// (~80–145 kB vs 550 kB for google-libphonenumber; bundle size matters for a
// PWA — S11e). Emails are lowercased. Both normalisations MUST agree with the
// SQL-side ldos_normalise_* functions, because the unique indexes on
// ldos_contact_identities are what actually guarantee idempotent imports.
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { supabase } from "@/lib/supabase";

export interface LdosContact {
  id: string;
  userId: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  title: string | null;
  notes: string | null;
  isFavourite: boolean;
  avatarUrl: string | null;
  mergedIntoId: string | null;
  memoryEntityId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LdosContactIdentity {
  id: string;
  contactId: string;
  source: "ghl" | "google" | "ringcentral" | "clickup" | "manual" | "vcard" | "device";
  externalId: string | null;
  kind: "email" | "phone" | "account";
  valueNormalised: string;
  isPrimary: boolean;
  lastSyncedAt: string | null;
}

export interface LdosMergeCandidate {
  contactId: string;
  candidateId: string;
  reason: string;
  createdAt: string;
}

export interface ContactCreateInput {
  displayName: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  notes?: string;
  email?: string;
  phone?: string;
  source?: LdosContactIdentity["source"];
  externalId?: string;
}

export interface ContactListParams {
  q?: string;
  favourite?: boolean;
  source?: string;
  limit?: number;
  cursor?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function mapContact(row: Row): LdosContact {
  return {
    id: row.id,
    userId: row.user_id,
    displayName: row.display_name,
    firstName: row.first_name ?? null,
    lastName: row.last_name ?? null,
    company: row.company ?? null,
    title: row.title ?? null,
    notes: row.notes ?? null,
    isFavourite: !!row.is_favourite,
    avatarUrl: row.avatar_url ?? null,
    mergedIntoId: row.merged_into_id ?? null,
    memoryEntityId: row.memory_entity_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapIdentity(row: Row): LdosContactIdentity {
  return {
    id: row.id,
    contactId: row.contact_id,
    source: row.source,
    externalId: row.external_id ?? null,
    kind: row.kind,
    valueNormalised: row.value_normalised,
    isPrimary: !!row.is_primary,
    lastSyncedAt: row.last_synced_at ?? null,
  };
}

/** Must agree with SQL ldos_normalise_email(). */
export function normaliseEmail(value: string | undefined | null): string | null {
  const v = (value ?? "").trim().toLowerCase();
  return v.length ? v : null;
}

/** E.164 via libphonenumber-js/min; must be at least as strict as the SQL side. */
export function normalisePhone(value: string | undefined | null, defaultCountry = "US"): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw, defaultCountry as never);
  if (parsed && parsed.isValid()) return parsed.number; // E.164
  // Unparseable numbers do NOT become identities. A half-parsed phone must
  // never merge two people (S11e).
  return null;
}

async function requireUserId(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const id = data.session?.user?.id;
  if (!id) throw new Error("Not signed in");
  return id;
}

/** Field-provenance stamp. Manual edits pin; sync writes do not. */
async function stampProvenance(
  contactId: string,
  fields: string[],
  source: string,
  pin: boolean
): Promise<void> {
  if (!fields.length) return;
  const rows = fields.map((field_name) => ({
    contact_id: contactId,
    field_name,
    source,
    written_at: new Date().toISOString(),
    is_pinned: pin,
  }));
  const { error } = await supabase
    .from("ldos_contact_field_provenance")
    .upsert(rows, { onConflict: "contact_id,field_name" });
  if (error) throw new Error(error.message);
}

/** Which fields are pinned (human-edited) on a contact — the clobber guard. */
export async function pinnedFields(contactId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("ldos_contact_field_provenance")
    .select("field_name")
    .eq("contact_id", contactId)
    .eq("is_pinned", true);
  if (error) throw new Error(error.message);
  return new Set((data ?? []).map((r: Row) => r.field_name));
}

export const ldosContactsService = {
  /** App-scoped list. THE only sanctioned read path (S11c/S11d). */
  async list(
    appSlug: string,
    params: ContactListParams = {}
  ): Promise<{ contacts: LdosContact[]; nextCursor: string | null }> {
    const { data, error } = await supabase.rpc("ldos_resolve_contact_access", {
      p_app_slug: appSlug,
    });
    if (error) throw new Error(error.message);
    let rows: Row[] = (data ?? []) as Row[];

    if (params.favourite !== undefined) rows = rows.filter((r) => !!r.is_favourite === params.favourite);
    if (params.q) {
      const q = params.q.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.display_name ?? "").toLowerCase().includes(q) ||
          (r.company ?? "").toLowerCase().includes(q)
      );
    }
    // Cursor = the last id of the previous page over the resolver's stable
    // (favourite DESC, display_name) ordering.
    if (params.cursor) {
      const at = rows.findIndex((r) => r.id === params.cursor);
      rows = at >= 0 ? rows.slice(at + 1) : rows;
    }
    const limit = Math.min(Math.max(params.limit ?? 100, 1), 500);
    const page = rows.slice(0, limit);
    const nextCursor = rows.length > limit ? page[page.length - 1].id : null;
    return { contacts: page.map(mapContact), nextCursor };
  },

  async get(appSlug: string, id: string): Promise<LdosContact> {
    // Resolve through the app-scoped view so a plugin cannot read a contact
    // its app was not granted, even by guessing the uuid.
    const { contacts } = await this.list(appSlug, { limit: 500 });
    const hit = contacts.find((c) => c.id === id);
    if (hit) return hit;
    // Large books: fall back to one resolver call + client filter is not
    // enough; check membership directly (RLS still applies).
    const { data, error } = await supabase
      .from("ldos_contact_app_links")
      .select("contact_id, ldos_contacts!inner(*)")
      .eq("app_slug", appSlug)
      .eq("visible", true)
      .eq("contact_id", id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("Contact not found or not granted to this app");
    return mapContact((data as Row).ldos_contacts);
  },

  /**
   * Create runs the S11e match ladder FIRST (rungs 1–3 resolve to the existing
   * contact; rung 4 records a merge candidate and still creates; rung 5 creates).
   */
  async create(appSlug: string, input: ContactCreateInput): Promise<LdosContact> {
    const userId = await requireUserId();
    const email = normaliseEmail(input.email);
    const phone = normalisePhone(input.phone);

    const { data: match, error: matchErr } = await supabase.rpc("ldos_contact_match", {
      p_email: email,
      p_phone: phone,
      p_source: input.source ?? "manual",
      p_external_id: input.externalId ?? null,
      p_name: input.displayName,
      p_company: input.company ?? null,
    });
    if (matchErr) throw new Error(matchErr.message);
    const rung: Row = Array.isArray(match) ? match[0] : match;

    // Rungs 1–3: the person exists. Attach any new identity, link the app, return.
    if (rung?.contact_id && (rung.disposition === "auto_merge" || rung.disposition === "update_in_place")) {
      const contactId: string = rung.contact_id;
      await attachIdentities(userId, contactId, input, email, phone);
      await linkApp(contactId, appSlug);
      return this.get(appSlug, contactId);
    }

    // Rung 5 (and rung 4, which creates AND queues): new canonical row.
    const { data: created, error: insErr } = await supabase
      .from("ldos_contacts")
      .insert({
        user_id: userId,
        display_name: input.displayName,
        first_name: input.firstName ?? null,
        last_name: input.lastName ?? null,
        company: input.company ?? null,
        title: input.title ?? null,
        notes: input.notes ?? null,
      })
      .select()
      .single();
    if (insErr) throw new Error(insErr.message);
    const contact = mapContact(created as Row);

    await attachIdentities(userId, contact.id, input, email, phone);
    await linkApp(contact.id, appSlug);
    await stampProvenance(
      contact.id,
      ["display_name", "first_name", "last_name", "company", "title", "notes"].filter(
        (f) => (input as Row)[snakeToCamel(f)] !== undefined || f === "display_name"
      ),
      input.source ?? "manual",
      (input.source ?? "manual") === "manual"
    );

    // Rung 4: same name + company, different/no email — queue it, NEVER auto-merge.
    if (rung?.disposition === "merge_queue" && rung.contact_id) {
      await supabase.from("plugin_records").upsert(
        {
          user_id: userId,
          plugin_id: "ldos-contacts",
          collection: "merge_queue",
          key: `${rung.contact_id}:${contact.id}`,
          data: {
            contactId: rung.contact_id,
            candidateId: contact.id,
            reason: "same name + same company, no shared email (ladder rung 4)",
            createdAt: new Date().toISOString(),
          },
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,plugin_id,collection,key" }
      );
    }
    return contact;
  },

  /** update() pins edited fields as source='manual' (S11d). */
  async update(appSlug: string, id: string, patch: Partial<ContactCreateInput>): Promise<LdosContact> {
    await this.get(appSlug, id); // membership check
    const upd: Row = {};
    if (patch.displayName !== undefined) upd.display_name = patch.displayName;
    if (patch.firstName !== undefined) upd.first_name = patch.firstName;
    if (patch.lastName !== undefined) upd.last_name = patch.lastName;
    if (patch.company !== undefined) upd.company = patch.company;
    if (patch.title !== undefined) upd.title = patch.title;
    if (patch.notes !== undefined) upd.notes = patch.notes;
    if (Object.keys(upd).length) {
      const { error } = await supabase.from("ldos_contacts").update(upd).eq("id", id);
      if (error) throw new Error(error.message);
      await stampProvenance(id, Object.keys(upd), "manual", true);
    }
    const userId = await requireUserId();
    const email = normaliseEmail(patch.email);
    const phone = normalisePhone(patch.phone);
    await attachIdentities(userId, id, patch as ContactCreateInput, email, phone);
    return this.get(appSlug, id);
  },

  /** Tombstone + unlink. NEVER propagates outward (S11f failure mode 3). */
  async remove(appSlug: string, id: string): Promise<void> {
    await this.get(appSlug, id);
    const userId = await requireUserId();
    const { data: idents } = await supabase
      .from("ldos_contact_identities")
      .select("source, external_id, kind, value_normalised")
      .eq("contact_id", id);
    const { error: tombErr } = await supabase.from("ldos_contact_tombstones").insert({
      user_id: userId,
      contact_id: id,
      reason: "user_delete",
      identities: idents ?? [],
    });
    if (tombErr) throw new Error(tombErr.message);
    // Soft-delete the canonical row; drop links so no app sees it.
    const { error } = await supabase
      .from("ldos_contacts")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw new Error(error.message);
    await supabase.from("ldos_contact_app_links").delete().eq("contact_id", id);
    // Identities are removed locally so the values can be reused; the
    // tombstone carries them for resurrection-suppression on inbound sync.
    await supabase.from("ldos_contact_identities").delete().eq("contact_id", id);
  },

  /** One flag, read by both Contacts and the Inbox (S2c). */
  async setFavourite(appSlug: string, id: string, value: boolean): Promise<void> {
    await this.get(appSlug, id);
    const { error } = await supabase
      .from("ldos_contacts")
      .update({ is_favourite: value })
      .eq("id", id);
    if (error) throw new Error(error.message);
  },

  /** Merge keeps BOTH rows: duplicate gets merged_into_id and its identities move. */
  async merge(appSlug: string, primaryId: string, duplicateId: string): Promise<LdosContact> {
    if (primaryId === duplicateId) throw new Error("Cannot merge a contact into itself");
    await this.get(appSlug, primaryId);
    await this.get(appSlug, duplicateId);
    const { error: moveErr } = await supabase
      .from("ldos_contact_identities")
      .update({ contact_id: primaryId })
      .eq("contact_id", duplicateId);
    if (moveErr) throw new Error(moveErr.message);
    const { error } = await supabase
      .from("ldos_contacts")
      .update({ merged_into_id: primaryId })
      .eq("id", duplicateId);
    if (error) throw new Error(error.message);
    await supabase.from("ldos_contact_app_links").delete().eq("contact_id", duplicateId);
    // Clear any queue entry for this pair (either direction).
    const userId = await requireUserId();
    await supabase
      .from("plugin_records")
      .delete()
      .eq("user_id", userId)
      .eq("plugin_id", "ldos-contacts")
      .eq("collection", "merge_queue")
      .in("key", [`${primaryId}:${duplicateId}`, `${duplicateId}:${primaryId}`]);
    return this.get(appSlug, primaryId);
  },

  async listMergeCandidates(): Promise<LdosMergeCandidate[]> {
    const userId = await requireUserId();
    const { data, error } = await supabase
      .from("plugin_records")
      .select("data")
      .eq("user_id", userId)
      .eq("plugin_id", "ldos-contacts")
      .eq("collection", "merge_queue");
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: Row) => r.data as LdosMergeCandidate);
  },

  /**
   * "These are different people" — a FIRST-CLASS outcome (S11e). Records a
   * negative match so the pair never returns to the queue.
   */
  async markNotDuplicate(contactId: string, candidateId: string): Promise<void> {
    const userId = await requireUserId();
    await supabase.from("plugin_records").upsert(
      {
        user_id: userId,
        plugin_id: "ldos-contacts",
        collection: "negative_matches",
        key: [contactId, candidateId].sort().join(":"),
        data: { contactId, candidateId, decidedAt: new Date().toISOString() },
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,plugin_id,collection,key" }
    );
    await supabase
      .from("plugin_records")
      .delete()
      .eq("user_id", userId)
      .eq("plugin_id", "ldos-contacts")
      .eq("collection", "merge_queue")
      .in("key", [`${contactId}:${candidateId}`, `${candidateId}:${contactId}`]);
  },

  async identities(appSlug: string, id: string): Promise<LdosContactIdentity[]> {
    await this.get(appSlug, id);
    const { data, error } = await supabase
      .from("ldos_contact_identities")
      .select("*")
      .eq("contact_id", id)
      .order("created_at");
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapIdentity);
  },

  /** vCard 3.0/4.0 import. Idempotent by the identity unique indexes. */
  async importVCard(
    appSlug: string,
    file: string | Blob
  ): Promise<{ created: number; merged: number; skipped: number }> {
    const text = typeof file === "string" ? file : await file.text();
    const cards = parseVCards(text);
    let created = 0, merged = 0, skipped = 0;
    for (const card of cards) {
      if (!card.displayName && !card.email && !card.phone) { skipped++; continue; }
      const before = await supabase.rpc("ldos_contact_match", {
        p_email: normaliseEmail(card.email),
        p_phone: normalisePhone(card.phone),
        p_source: "vcard",
        p_external_id: card.uid ?? null,
        p_name: card.displayName ?? null,
        p_company: card.company ?? null,
      });
      const rung: Row | undefined = Array.isArray(before.data) ? before.data[0] : before.data;
      const existed = rung?.disposition === "auto_merge" || rung?.disposition === "update_in_place";
      await this.create(appSlug, {
        displayName: card.displayName ?? card.email ?? card.phone ?? "Unknown",
        firstName: card.firstName,
        lastName: card.lastName,
        company: card.company,
        title: card.title,
        email: card.email,
        phone: card.phone,
        source: "vcard",
        externalId: card.uid,
      });
      if (existed) merged++; else created++;
    }
    return { created, merged, skipped };
  },

  /** Hand-rolled serializer for the known field set (S11f: vCard 3.0). */
  async exportVCard(appSlug: string, ids?: string[]): Promise<Blob> {
    const { contacts } = await this.list(appSlug, { limit: 500 });
    const chosen = ids?.length ? contacts.filter((c) => ids.includes(c.id)) : contacts;
    const lines: string[] = [];
    for (const c of chosen) {
      const idents = await this.identities(appSlug, c.id);
      lines.push("BEGIN:VCARD", "VERSION:3.0");
      lines.push(`FN:${escapeVCard(c.displayName)}`);
      lines.push(`N:${escapeVCard(c.lastName ?? "")};${escapeVCard(c.firstName ?? "")};;;`);
      if (c.company || c.title) {
        if (c.company) lines.push(`ORG:${escapeVCard(c.company)}`);
        if (c.title) lines.push(`TITLE:${escapeVCard(c.title)}`);
      }
      for (const i of idents) {
        if (i.kind === "email") lines.push(`EMAIL;TYPE=INTERNET:${i.valueNormalised}`);
        if (i.kind === "phone") lines.push(`TEL;TYPE=CELL:${i.valueNormalised}`);
      }
      if (c.notes) lines.push(`NOTE:${escapeVCard(c.notes)}`);
      lines.push(`UID:ldos-${c.id}`, "END:VCARD");
    }
    return new Blob([lines.join("\r\n") + "\r\n"], { type: "text/vcard" });
  },
};

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

async function linkApp(contactId: string, appSlug: string): Promise<void> {
  // Opt-in visibility: link ONLY the creating app (S11c). Ignore conflicts —
  // an existing link means the app already sees the contact.
  const { error } = await supabase
    .from("ldos_contact_app_links")
    .upsert(
      { contact_id: contactId, app_slug: appSlug, visible: true },
      { onConflict: "contact_id,app_slug" }
    );
  if (error) throw new Error(error.message);
}

async function attachIdentities(
  userId: string,
  contactId: string,
  input: Partial<ContactCreateInput>,
  email: string | null,
  phone: string | null
): Promise<void> {
  const rows: Row[] = [];
  const source = input.source ?? "manual";
  if (email)
    rows.push({ user_id: userId, contact_id: contactId, source, kind: "email",
      external_id: input.externalId ?? null, value_normalised: email });
  if (phone)
    rows.push({ user_id: userId, contact_id: contactId, source, kind: "phone",
      external_id: input.externalId ?? null, value_normalised: phone });
  if (!rows.length) return;
  // ignoreDuplicates: the unique indexes are the idempotency guarantee —
  // importing the same vCard twice must create zero new rows.
  const { error } = await supabase
    .from("ldos_contact_identities")
    .upsert(rows, { onConflict: "user_id,kind,value_normalised", ignoreDuplicates: true });
  if (error && !/duplicate|unique/i.test(error.message)) throw new Error(error.message);
}

interface ParsedVCard {
  uid?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  email?: string;
  phone?: string;
}

/** Minimal, tolerant vCard 3.0/4.0 parser for the known field set. */
export function parseVCards(text: string): ParsedVCard[] {
  const cards: ParsedVCard[] = [];
  // Unfold continuation lines (RFC 6350 §3.2): CRLF followed by space/tab.
  const unfolded = text.replace(/\r?\n[ \t]/g, "");
  let current: ParsedVCard | null = null;
  for (const raw of unfolded.split(/\r?\n/)) {
    const line = raw.trim();
    if (/^BEGIN:VCARD$/i.test(line)) { current = {}; continue; }
    if (/^END:VCARD$/i.test(line)) { if (current) cards.push(current); current = null; continue; }
    if (!current) continue;
    const sep = line.indexOf(":");
    if (sep < 1) continue;
    const key = line.slice(0, sep).split(";")[0].toUpperCase();
    const value = unescapeVCard(line.slice(sep + 1));
    switch (key) {
      case "UID": current.uid = value; break;
      case "FN": current.displayName = value; break;
      case "N": {
        const [last, first] = value.split(";");
        current.lastName = last || undefined;
        current.firstName = first || undefined;
        break;
      }
      case "ORG": current.company = value.split(";")[0] || undefined; break;
      case "TITLE": current.title = value; break;
      case "EMAIL": if (!current.email) current.email = value; break;
      case "TEL": if (!current.phone) current.phone = value; break;
    }
  }
  return cards;
}

function escapeVCard(v: string): string {
  return v.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}
function unescapeVCard(v: string): string {
  return v.replace(/\\n/gi, "\n").replace(/\\,/g, ",").replace(/\\;/g, ";").replace(/\\\\/g, "\\");
}
